// ═══════════════════════════════════════════════════════════════════════════
// AdminDashboard — tableau de bord d'usage, servi sur une URL discrète et
// protégé par un mot de passe unique (jamais dans le code : il est saisi ici,
// gardé en sessionStorage le temps de l'onglet, et vérifié par le serveur à
// chaque requête via l'en-tête x-admin-key). Données : GET /api/admin/stats.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import AdminUsers from "./AdminUsers";

const STORAGE_KEY = "dispo_admin_key";
const API_BASE = import.meta.env.VITE_API_BASE || "/api";
const PALETTE = ["#38bdf8", "#a78bfa", "#22c55e", "#f59e0b", "#f472b6", "#f87171", "#2dd4bf"];

const fmt = (n) => (n == null ? "–" : new Intl.NumberFormat("fr-FR").format(n));
const pct = (part, total) => (total ? `${Math.round((part / total) * 100)} %` : "–");
const dayLabel = (d) => {
  const [, m, day] = d.split("-");
  return `${day}/${m}`;
};
const dateTime = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

async function fetchStats(key, days) {
  const res = await fetch(`${API_BASE}/admin/stats?days=${days}`, {
    headers: { "x-admin-key": key },
  });
  if (res.status === 401) throw Object.assign(new Error("Mot de passe invalide"), { unauthorized: true });
  if (!res.ok) throw new Error(`Erreur serveur (${res.status})`);
  return res.json();
}

const Card = ({ title, children, className = "" }) => (
  <section className={`rounded-2xl bg-slate-900/70 border border-slate-800 p-5 ${className}`}>
    {title && <h2 className="text-sm font-semibold text-slate-300 mb-4 tracking-wide uppercase">{title}</h2>}
    {children}
  </section>
);

const Kpi = ({ label, value, sub }) => (
  <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4">
    <div className="text-xs text-slate-400">{label}</div>
    <div className="text-2xl font-bold text-white mt-1">{fmt(value)}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const chartCommon = {
  grid: <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />,
  xAxis: (
    <XAxis dataKey="day" tickFormatter={dayLabel} stroke="#64748b" fontSize={11} minTickGap={24} />
  ),
  yAxis: <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} width={32} />,
  tooltip: (
    <Tooltip
      contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, fontSize: 12 }}
      labelFormatter={(d) => `Le ${dayLabel(d)}`}
    />
  ),
};

const SeriesArea = ({ data, name, color }) => (
  <ResponsiveContainer width="100%" height={220}>
    <AreaChart data={data}>
      {chartCommon.grid}
      {chartCommon.xAxis}
      {chartCommon.yAxis}
      {chartCommon.tooltip}
      <Area type="monotone" dataKey="count" name={name} stroke={color} fill={color} fillOpacity={0.2} strokeWidth={2} />
    </AreaChart>
  </ResponsiveContainer>
);

const SeriesBars = ({ data, name, color }) => (
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={data}>
      {chartCommon.grid}
      {chartCommon.xAxis}
      {chartCommon.yAxis}
      {chartCommon.tooltip}
      <Bar dataKey="count" name={name} fill={color} radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const Split = ({ data, labels = {} }) => {
  const rows = Object.entries(data || {}).map(([k, v]) => ({ name: labels[k] || k, value: v }));
  const total = rows.reduce((a, r) => a + r.value, 0);
  if (!rows.length) return <div className="text-sm text-slate-500">Aucune donnée</div>;
  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width={120} height={120}>
        <PieChart>
          <Pie data={rows} dataKey="value" innerRadius={34} outerRadius={56} paddingAngle={2}>
            {rows.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <ul className="text-sm space-y-1 flex-1">
        {rows.map((r, i) => (
          <li key={r.name} className="flex justify-between gap-3">
            <span className="flex items-center gap-2 text-slate-300">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
              {r.name}
            </span>
            <span className="text-slate-400">
              {fmt(r.value)} · {pct(r.value, total)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Table = ({ columns, rows, empty = "Rien pour l'instant" }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-slate-500">
          {columns.map((c) => (
            <th key={c.key} className="pb-2 pr-3 font-medium">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="py-3 text-slate-500">{empty}</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-800/80">
            {columns.map((c) => (
              <td key={c.key} className="py-2 pr-3 text-slate-200 whitespace-nowrap">
                {c.render ? c.render(r[c.key], r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function Login({ onSubmit, error, busy }) {
  const [value, setValue] = useState("");
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(value);
        }}
        className="w-full max-w-sm rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4"
      >
        <h1 className="text-lg font-semibold text-white">Dispo · tableau de bord</h1>
        <input
          type="password"
          autoFocus
          autoComplete="current-password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Mot de passe"
          className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 text-white outline-none focus:border-sky-400"
        />
        {error && <div className="text-sm text-red-400">{error}</div>}
        <button
          type="submit"
          disabled={busy || !value}
          className="w-full rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-semibold py-3"
        >
          {busy ? "Vérification…" : "Entrer"}
        </button>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const [key, setKey] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview"); // "overview" | "users"

  // Page privée : pas d'indexation, et titre d'onglet dédié.
  useEffect(() => {
    document.title = "Dispo · tableau de bord";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const load = async (k, d) => {
    setBusy(true);
    setError("");
    try {
      const json = await fetchStats(k, d);
      setData(json);
      setKey(k);
      try {
        sessionStorage.setItem(STORAGE_KEY, k);
      } catch {
        /* stockage indisponible : la clé vit en mémoire */
      }
    } catch (e) {
      if (e.unauthorized) {
        setKey("");
        setData(null);
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
      setError(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setKey("");
    setData(null);
  };

  useEffect(() => {
    if (key) load(key, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const cumulativeSignups = useMemo(() => {
    if (!data) return [];
    let acc = data.totals.users - data.series.signups.reduce((a, r) => a + r.count, 0);
    return data.series.signups.map((r) => ({ day: r.day, count: (acc += r.count) }));
  }, [data]);

  if (!key || !data) {
    return <Login onSubmit={(v) => load(v, days)} error={error} busy={busy} />;
  }

  const { totals, activity, engagement, splits, series, lists } = data;
  const sum = (s) => s.reduce((a, r) => a + r.count, 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Dispo · tableau de bord</h1>
          <p className="text-xs text-slate-500">
            Généré le {dateTime(data.generatedAt)} · bots de démo exclus · jours en heure de Paris
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-slate-700 overflow-hidden mr-2">
            {[["overview", "Vue d'ensemble"], ["users", "Utilisateurs"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-3 py-1.5 text-sm ${tab === k ? "bg-slate-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}
              >
                {l}
              </button>
            ))}
          </div>
          {tab === "overview" && [7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                days === d ? "bg-sky-500 text-slate-950 border-sky-500" : "border-slate-700 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {d} j
            </button>
          ))}
          <button
            onClick={() => load(key, days)}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "…" : "Rafraîchir"}
          </button>
          <button
            onClick={logout}
            className="px-3 py-1.5 rounded-lg text-sm text-slate-500 hover:text-slate-300"
          >
            Quitter
          </button>
        </div>
      </header>

      {error && <div className="mb-4 text-sm text-red-400">{error}</div>}

      {tab === "users" && <AdminUsers apiKey={key} onUnauthorized={logout} />}

      {tab === "overview" && (
      <>
      {/* Utilisateurs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        <Kpi label="Utilisateurs" value={totals.users} />
        <Kpi label="Inscrits aujourd'hui" value={activity.signupsToday} />
        <Kpi label="Inscrits 7 jours" value={activity.signups7d} />
        <Kpi label="Inscrits 30 jours" value={activity.signups30d} />
        <Kpi label="Actifs 24 h" value={activity.dau} sub="ont ouvert l'app" />
        <Kpi label="Actifs 7 j / 30 j" value={activity.wau} sub={`${fmt(activity.mau)} sur 30 jours`} />
      </div>

      {/* Contenu */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-6">
        <Kpi label="Dispos" value={totals.dispos} sub={`${fmt(totals.upcomingDispos)} à venir`} />
        <Kpi label="Sondages" value={totals.polls} sub={`${fmt(totals.votes)} votes`} />
        <Kpi label="Crews" value={totals.crews} />
        <Kpi label="Messages" value={totals.messages} sub={`${fmt(totals.reactions)} réactions`} />
        <Kpi label="Partages de position" value={totals.liveShares} />
        <Kpi label="Amitiés" value={totals.friendships} sub={`${engagement.avgFriendsPerUser} amis / utilisateur`} />
        <Kpi label="Réponses invités (web)" value={totals.guestResponses} />
        <Kpi label="Appareils avec notifs" value={totals.pushUsers} sub={`${fmt(totals.pushTokens)} jetons`} />
      </div>

      {/* Courbes */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card title={`Nouveaux inscrits par jour · ${fmt(sum(series.signups))} sur ${days} j`}>
          <SeriesBars data={series.signups} name="Inscrits" color="#38bdf8" />
        </Card>
        <Card title="Utilisateurs cumulés">
          <SeriesArea data={cumulativeSignups} name="Utilisateurs" color="#a78bfa" />
        </Card>
        <Card title={`Messages par jour · ${fmt(sum(series.messages))}`}>
          <SeriesArea data={series.messages} name="Messages" color="#22c55e" />
        </Card>
        <Card title="Utilisateurs ayant écrit, par jour">
          <SeriesBars data={series.activeSenders} name="Auteurs" color="#2dd4bf" />
        </Card>
        <Card title={`Dispos et sondages créés par jour · ${fmt(sum(series.dispos))} dispos, ${fmt(sum(series.polls))} sondages`}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={series.dispos.map((r, i) => ({ day: r.day, dispos: r.count, sondages: series.polls[i]?.count ?? 0 }))}>
              {chartCommon.grid}
              {chartCommon.xAxis}
              {chartCommon.yAxis}
              {chartCommon.tooltip}
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="dispos" name="Dispos" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="sondages" name="Sondages" stroke="#f472b6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title={`Partages de position, crews, réponses invités · ${days} j`}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={series.liveShares.map((r, i) => ({
                day: r.day,
                positions: r.count,
                crews: series.crews[i]?.count ?? 0,
                invites: series.guestResponses[i]?.count ?? 0,
              }))}
            >
              {chartCommon.grid}
              {chartCommon.xAxis}
              {chartCommon.yAxis}
              {chartCommon.tooltip}
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="positions" name="Positions" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="crews" name="Crews" stroke="#a78bfa" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="invites" name="Réponses invités" stroke="#22c55e" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Répartitions + engagement */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Card title="Connexion">
          <Split data={splits.providers} labels={{ APPLE: "Apple", GOOGLE: "Google", PASSWORD: "Mot de passe" }} />
        </Card>
        <Card title="Appareils actifs 30 j">
          <Split data={splits.devices30d} labels={{ ios: "iPhone", android: "Android" }} />
        </Card>
        <Card title="Langue">
          <Split data={splits.locales} labels={{ fr: "Français", en: "Anglais" }} />
        </Card>
        <Card title="Types de messages">
          <Split
            data={splits.messageTypes}
            labels={{ TEXT: "Texte", IMAGE: "Photo", VIDEO: "Vidéo", AUDIO: "Vocal", GIF: "GIF", LOCATION: "Position", SYSTEM: "Système", AVAILABILITY_CARD: "Carte dispo" }}
          />
        </Card>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        <Kpi label="Ont au moins un ami" value={engagement.usersWithFriend} sub={pct(engagement.usersWithFriend, totals.users)} />
        <Kpi label="Dans au moins un crew" value={engagement.usersInCrew} sub={pct(engagement.usersInCrew, totals.users)} />
        <Kpi label="Ont créé une dispo" value={engagement.usersWithDispo} sub={pct(engagement.usersWithDispo, totals.users)} />
        <Kpi label="Ont envoyé un message" value={engagement.usersWithMessage} sub={pct(engagement.usersWithMessage, totals.users)} />
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <Kpi label="Demandes d'ami en attente" value={splits.friendRequests.PENDING || 0} sub={`${fmt(splits.friendRequests.ACCEPTED || 0)} acceptées`} />
        <Kpi label="Blocages" value={totals.blocks} sub={`${fmt(splits.reports.PENDING || 0)} signalements à traiter`} />
        <Kpi label="Tickets support" value={totals.supportTickets} />
      </div>

      {/* Listes */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Derniers inscrits" className="lg:col-span-1">
          <Table
            columns={[
              { key: "login", label: "Login" },
              { key: "createdAt", label: "Le", render: dateTime },
              { key: "provider", label: "Via", render: (v) => ({ APPLE: "Apple", GOOGLE: "Google", PASSWORD: "Mdp" }[v] || "–") },
              { key: "device", label: "Appareil", render: (v) => v || "–" },
              { key: "friends", label: "Amis" },
            ]}
            rows={lists.recentSignups}
          />
        </Card>
        <Card title={`Plus actifs sur ${days} j`}>
          <Table
            columns={[
              { key: "login", label: "Login" },
              { key: "messages", label: "Messages", render: fmt },
            ]}
            rows={lists.mostActiveUsers}
          />
        </Card>
        <Card title="Plus gros crews">
          <Table
            columns={[
              { key: "name", label: "Crew" },
              { key: "members", label: "Membres", render: fmt },
              { key: "createdAt", label: "Créé le", render: (v) => new Date(v).toLocaleDateString("fr-FR") },
            ]}
            rows={lists.topCrews}
          />
        </Card>
      </div>
      </>
      )}
    </div>
  );
}
